import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Field, ORG_TYPE_LABEL, slugify } from "./_shared";

/* ---------------------------------------------------------------------------
 * Create-client dialog — captures the minimum required to bootstrap.
 * ------------------------------------------------------------------------- */
export function CreateClientDialog({ open, onOpenChange, orgType, onCreated }) {
  const [name, setName] = useState("");
  const [town, setTown] = useState("");
  const [slug, setSlug] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [color, setColor] = useState("#0038FF");
  // iter143 — Automated Gameweek Engine seed values.
  const [gameweek, setGameweek] = useState("1");
  const [matchCount, setMatchCount] = useState("6");
  const [dayCount, setDayCount] = useState("3");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(""); setTown(""); setSlug(""); setContactName(""); setContactPhone("");
      setContactEmail(""); setLoginEmail(""); setLoginPassword(""); setColor("#0038FF");
      setGameweek("1"); setMatchCount("6"); setDayCount("3");
    }
  }, [open]);

  const isSingle = orgType !== "pub_group";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name,
        contact_email: contactEmail || null,
        client_email: loginEmail,
        client_password: loginPassword,
        org_type: orgType,
        town: town || null,
        contact_name: contactName || null,
        contact_phone: contactPhone || null,
        primary_color: color,
        // iter143 — Automated Gameweek Engine seed. Blank input → null
        // (banner stays hidden). Non-blank → int.
        current_gameweek_number: gameweek === "" ? null : Math.max(0, Number(gameweek) || 0),
        match_count: matchCount === "" ? null : Math.max(0, Number(matchCount) || 0),
        day_count: dayCount === "" ? null : Math.max(0, Number(dayCount) || 0),
      };
      if (isSingle) payload.slug = slug || slugify(name);
      const { data } = await api.post("/clients", payload);
      toast.success(`${ORG_TYPE_LABEL[orgType]} "${name}" created`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-lg" data-testid="create-client-dialog">
        <DialogHeader>
          <DialogTitle>New {ORG_TYPE_LABEL[orgType] || "Client"}</DialogTitle>
          <DialogDescription>Bootstrap the record — you can edit everything else afterwards.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={isSingle ? "Venue name" : "Group name"}>
            <Input required value={name} onChange={(e) => { setName(e.target.value); if (isSingle && !slug) setSlug(slugify(e.target.value)); }} data-testid="cc-name" className="h-10 rounded-sm border-zinc-300" />
          </Field>
          {isSingle && (
            <Field label="Town">
              <Input value={town} onChange={(e) => setTown(e.target.value)} data-testid="cc-town" className="h-10 rounded-sm border-zinc-300" />
            </Field>
          )}
          {isSingle && (
            <Field label="Slug" hint="Public URL: /[slug]">
              <Input required value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} data-testid="cc-slug" className="h-10 rounded-sm border-zinc-300 font-mono text-sm" />
            </Field>
          )}
          <Field label="Contact name">
            <Input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="cc-contact-name" className="h-10 rounded-sm border-zinc-300" />
          </Field>
          <Field label="Contact phone">
            <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} data-testid="cc-contact-phone" className="h-10 rounded-sm border-zinc-300" />
          </Field>
          <Field label="Contact email">
            <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} data-testid="cc-contact-email" className="h-10 rounded-sm border-zinc-300" />
          </Field>
          <Field label="Brand colour">
            <div className="flex items-center gap-2">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} data-testid="cc-color-picker" className="h-10 w-14 rounded-sm border border-zinc-300 cursor-pointer p-0.5" />
              <Input value={color} onChange={(e) => setColor(e.target.value)} className="h-10 rounded-sm border-zinc-300 font-mono uppercase text-sm" />
            </div>
          </Field>
          <div className="md:col-span-2 border-t border-zinc-100 pt-4 mt-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">
              Automated Gameweek Engine <span className="text-zinc-400 normal-case tracking-normal font-medium">· super-admin only</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Current gameweek #" hint="Auto-increments on close season">
                <Input type="number" min="0" max="9999" value={gameweek} onChange={(e) => setGameweek(e.target.value)} data-testid="cc-gameweek" className="h-10 rounded-sm border-zinc-300 tabular-nums" />
              </Field>
              <Field label="Match count" hint="Default 6">
                <Input type="number" min="0" max="999" value={matchCount} onChange={(e) => setMatchCount(e.target.value)} data-testid="cc-match-count" className="h-10 rounded-sm border-zinc-300 tabular-nums" />
              </Field>
              <Field label="Day count" hint="Default 3">
                <Input type="number" min="0" max="999" value={dayCount} onChange={(e) => setDayCount(e.target.value)} data-testid="cc-day-count" className="h-10 rounded-sm border-zinc-300 tabular-nums" />
              </Field>
            </div>
          </div>

          <div className="md:col-span-2 border-t border-zinc-100 pt-4 mt-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Client login (they use these to sign in)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Login email">
                <Input required type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} data-testid="cc-login-email" className="h-10 rounded-sm border-zinc-300" />
              </Field>
              <Field label="Login password">
                <Input required type="text" minLength={6} value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} data-testid="cc-login-password" className="h-10 rounded-sm border-zinc-300 font-mono text-sm" />
              </Field>
            </div>
          </div>
          <DialogFooter className="md:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="cc-submit" className="rounded-sm bg-zinc-950 hover:bg-black text-white">
              {saving ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
